import React from "react";
import FloatingCard from "../../ui/FloatingCard";
import { useNavigate } from "react-router-dom";
import { eid } from "../../../utils/entityId";
import { eventTimeZone, formatEventTimeForViewer } from "../../../utils/eventTime";

/**
 * Team Overview Tab Component
 * Displays team info and upcoming matches
 */
const TeamOverviewTab = ({
  team,
  rosterSummary,
  scheduleMatches,
  loadingMatches,
}) => {
  const navigate = useNavigate();

  const getMatchStatusStyles = (status) => {
    const statusMap = {
      in_progress: {
        leftBorderColor: "#ff3b30",
        borderColor: "rgba(255, 59, 48, 0.6)",
        borderColorHover: "rgba(255, 59, 48, 0.9)",
        backgroundColor: "rgba(255, 59, 48, 0.15)",
        backgroundColorHover: "rgba(255, 59, 48, 0.25)",
        textColor: "#ff3b30",
        statusIcon: "🔴",
        statusLabel: "IN PROGRESS",
      },
      scheduled: {
        leftBorderColor: "#007aff",
        borderColor: "rgba(0, 122, 255, 0.6)",
        borderColorHover: "rgba(0, 122, 255, 0.9)",
        backgroundColor: "rgba(0, 122, 255, 0.15)",
        backgroundColorHover: "rgba(0, 122, 255, 0.25)",
        textColor: "#007aff",
        statusIcon: "📅",
        statusLabel: "SCHEDULED",
      },
      ready: {
        leftBorderColor: "#007aff",
        borderColor: "rgba(0, 122, 255, 0.6)",
        borderColorHover: "rgba(0, 122, 255, 0.9)",
        backgroundColor: "rgba(0, 122, 255, 0.15)",
        backgroundColorHover: "rgba(0, 122, 255, 0.25)",
        textColor: "#007aff",
        statusIcon: "📅",
        statusLabel: "UPCOMING",
      },
      completed: {
        leftBorderColor: "#34d399",
        borderColor: "rgba(52, 211, 153, 0.6)",
        borderColorHover: "rgba(52, 211, 153, 0.9)",
        backgroundColor: "rgba(52, 211, 153, 0.15)",
        backgroundColorHover: "rgba(52, 211, 153, 0.25)",
        textColor: "#34d399",
        statusIcon: "✅",
        statusLabel: "COMPLETED",
      },
      default: {
        leftBorderColor: "#00ff88",
        borderColor: "rgba(0, 255, 136, 0.4)",
        borderColorHover: "rgba(0, 255, 136, 0.8)",
        backgroundColor: "rgba(0, 255, 136, 0.08)",
        backgroundColorHover: "rgba(0, 255, 136, 0.15)",
        textColor: "#00ff88",
        statusIcon: "📋",
        statusLabel: "MATCH",
      },
    };
    return statusMap[status] || statusMap.default;
  };

  return (
    <div className="overview-content-gaming">
      <div className="overview-grid-gaming">
        {/* Team Information Card */}
        <FloatingCard className="team-info-card-gaming delay-4">
          <div className="card-header-gaming">
            <h3 className="card-title-gaming">
              <span className="title-icon">📋</span>
              Team Information
            </h3>
          </div>
          <div className="card-content-gaming">
            <div className="info-grid-gaming">
              <div className="info-item-gaming">
                <span className="info-label-gaming">Team Type:</span>
                <span className="info-value-gaming">
                  <span className="value-icon">{team.is_public ? "🌐" : "🔒"}</span>
                  {team.is_public ? "Public" : "Private"}
                </span>
              </div>
              <div className="info-item-gaming">
                <span className="info-label-gaming">Join Approval:</span>
                <span className="info-value-gaming">
                  <span className="value-icon">{team.requires_approval ? "✋" : "✅"}</span>
                  {team.requires_approval ? "Required" : "Automatic"}
                </span>
              </div>
              <div className="info-item-gaming">
                <span className="info-label-gaming">Starter Slots:</span>
                <span className="info-value-gaming">
                  <span className="value-icon">🏃</span>
                  {team.settings?.max_members ?? team.max_members ?? "Unlimited"}
                </span>
              </div>
              <div className="info-item-gaming">
                <span className="info-label-gaming">Substitute Slots:</span>
                <span className="info-value-gaming">
                  <span className="value-icon">🔄</span>
                  {team.settings?.max_substitutes ??
                    team.max_substitutes ??
                    team.settings?.rules?.substitutions ??
                    team.rules?.substitutions ??
                    0}
                </span>
              </div>
              {rosterSummary && (
                <div className="info-item-gaming" style={{ gridColumn: "1 / -1" }}>
                  <span className="info-label-gaming">Team Roster:</span>
                  <div
                    className="roster-summary-gaming"
                    style={{
                      display: "flex",
                      gap: "1.5rem",
                      marginTop: "0.5rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      className="roster-stat-gaming"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.5rem 1rem",
                        background: "rgba(0, 255, 136, 0.1)",
                        border: "1px solid rgba(0, 255, 136, 0.3)",
                        borderRadius: "8px",
                      }}
                    >
                      <span style={{ fontSize: "1.2rem" }}>🏃</span>
                      <div>
                        <div style={{ fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.6)" }}>
                          Starters
                        </div>
                        <div style={{ fontSize: "1.1rem", fontWeight: "600", color: "#00ff88" }}>
                          {rosterSummary.primaryCount}/{rosterSummary.maxPrimary}
                        </div>
                      </div>
                    </div>
                    <div
                      className="roster-stat-gaming"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.5rem 1rem",
                        background: "rgba(255, 176, 0, 0.1)",
                        border: "1px solid rgba(255, 176, 0, 0.3)",
                        borderRadius: "8px",
                      }}
                    >
                      <span style={{ fontSize: "1.2rem" }}>🔄</span>
                      <div>
                        <div style={{ fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.6)" }}>
                          Substitutes
                        </div>
                        <div style={{ fontSize: "1.1rem", fontWeight: "600", color: "#ffb000" }}>
                          {rosterSummary.substituteCount}/{rosterSummary.maxSubstitutes}
                        </div>
                      </div>
                    </div>
                    <div
                      className="roster-stat-gaming"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.5rem 1rem",
                        background: "rgba(138, 180, 248, 0.1)",
                        border: "1px solid rgba(138, 180, 248, 0.3)",
                        borderRadius: "8px",
                      }}
                    >
                      <span style={{ fontSize: "1.2rem" }}>📊</span>
                      <div>
                        <div style={{ fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.6)" }}>
                          Total
                        </div>
                        <div style={{ fontSize: "1.1rem", fontWeight: "600", color: "#8ab4f8" }}>
                          {rosterSummary.totalActive}/{rosterSummary.maxPrimary + rosterSummary.maxSubstitutes}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="info-item-gaming">
                <span className="info-label-gaming">Created:</span>
                <span className="info-value-gaming">
                  <span className="value-icon">📅</span>
                  {new Date(team.created_at).toLocaleDateString()}
                </span>
              </div>
              {team.location && (
                <div className="info-item-gaming">
                  <span className="info-label-gaming">Location:</span>
                  <span className="info-value-gaming">
                    <span className="value-icon">📍</span>
                    {team.location}
                  </span>
                </div>
              )}
              {(team.created_by) ? (
                <div className="info-item-gaming">
                  <span className="info-label-gaming">Team Owner:</span>
                  <span className="info-value-gaming">
                    <span className="value-icon">👤</span>
                    <span
                      onClick={() => navigate(`/players/${team.owner_uuid || team.created_by}`)}
                      style={{ cursor: 'pointer', color: '#00e077', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                    >
                      {(team.owner_name && team.owner_name.trim())
                        ? team.owner_name
                        : (team.owner_username ? `@${team.owner_username}` : (team.owner_email || `Player #${team.created_by}`))}
                    </span>
                  </span>
                </div>
              ) : null}
            </div>

            {team.description && (
              <div className="description-section-gaming" style={{ marginTop: "1rem" }}>
                <h4 className="section-title-gaming" style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.4rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>About Team</h4>
                <p className="description-text-gaming" style={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.5, fontSize: "0.9rem", margin: 0 }}>{team.description}</p>
              </div>
            )}
          </div>
        </FloatingCard>

        {/* Matches Card */}
        <FloatingCard className="upcoming-matches-card-gaming delay-5">
          <div className="card-header-gaming">
            <h3 className="card-title-gaming">
              <span className="title-icon">📅</span>
              Matches{" "}
              {Array.isArray(scheduleMatches) && scheduleMatches.length > 0
                ? `(${scheduleMatches.length})`
                : ""}
            </h3>
          </div>
          <div
            className="card-content-gaming"
            style={{
              maxHeight: "350px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loadingMatches ? (
              <div style={{ textAlign: "center", padding: "20px", color: "#00ff88" }}>
                Loading matches...
              </div>
            ) : Array.isArray(scheduleMatches) && scheduleMatches.length > 0 ? (
              <div
                className="matches-list-gaming"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  overflow: "auto",
                  paddingRight: "8px",
                  flex: 1,
                }}
              >
                {scheduleMatches.map((match, index) => {
                  const statusStyles = getMatchStatusStyles(match.status);
                  return (
                    <div
                      key={match.id}
                      className={`match-item-gaming delay-${(index % 6) + 4}`}
                      style={{
                        padding: "12px 14px",
                        paddingLeft: "10px",
                        border: `1px solid ${statusStyles.borderColor}`,
                        borderLeft: `4px solid ${statusStyles.leftBorderColor}`,
                        borderRadius: "6px",
                        backgroundColor: statusStyles.backgroundColor,
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = statusStyles.borderColorHover;
                        e.currentTarget.style.backgroundColor = statusStyles.backgroundColorHover;
                        e.currentTarget.style.borderLeftColor = statusStyles.leftBorderColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = statusStyles.borderColor;
                        e.currentTarget.style.backgroundColor = statusStyles.backgroundColor;
                        e.currentTarget.style.borderLeftColor = statusStyles.leftBorderColor;
                      }}
                      onClick={() => navigate(`/matches/${eid(match)}`)}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: "600",
                            marginBottom: "4px",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          vs {match.opponent_name || "TBD"}
                          <span
                            style={{
                              fontSize: "0.65rem",
                              padding: "3px 8px",
                              borderRadius: "3px",
                              backgroundColor: statusStyles.textColor ? statusStyles.textColor + "33" : "rgba(0, 255, 136, 0.2)",
                              color: statusStyles.textColor,
                              fontWeight: "700",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {statusStyles.statusIcon} {statusStyles.statusLabel}
                          </span>
                        </div>
                        <div style={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)" }}>
                          {formatEventTimeForViewer(match.scheduled_at, eventTimeZone(match), {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        {(match.league_name || match.tournament_name) && (
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: statusStyles.textColor,
                              marginTop: "2px",
                              fontWeight: "500",
                            }}
                          >
                            {match.league_name || match.tournament_name}
                          </div>
                        )}
                      </div>
                      {match.venue && (
                        <div
                          style={{
                            fontSize: "0.85rem",
                            color: "rgba(255, 255, 255, 0.6)",
                            textAlign: "right",
                            marginLeft: "10px",
                            maxWidth: "150px",
                          }}
                        >
                          📍 {match.venue}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "20px", color: "rgba(255, 255, 255, 0.5)" }}>
                No matches scheduled
              </div>
            )}
          </div>
        </FloatingCard>
      </div>
    </div>
  );
};

export default TeamOverviewTab;
